import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Receipt } from 'lucide-react';
import { Navbar, Footer } from '@/components/Layout';
import { CodeBlock } from '@/components/CodeBlock';
import { useX402Config, isMainnet } from '@/lib/x402Config';
import { PAY_INSTALL, PAY_GATE_PY, PAY_STEPS, PAY_RESPONSES, PAY_FAQ } from '@/lib/pricingDocs';
import { GateProposedNotice, GATE_OPEN } from '@/components/binding/GateProposedNotice';

const fade = (i = 0) => ({ initial: { opacity: 0, y: 14 }, animate: { opacity: 1, y: 0 }, transition: { delay: 0.06 * i } });

function PriceTable({ config }) {
  const rows = [
    ['Binding Action Gate: execute', 'Not in service (proposed)', 'price-gate-execute'],
    ['Binding Action Gate: refuse', 'Not in service (proposed)', 'price-gate-refuse'],
    ['Human signing a consent receipt', 'Not in service (proposed)', 'price-human-sign'],
    ...(config?.premium_endpoints || []).map((e) => [`Premium AI: ${e.path.split('/')[3].replace(/-/g, ' ')}`, `${e.price} USDC`, `price-${e.path.split('/')[3]}`]),
  ];
  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden" data-testid="pricing-table">
      {rows.map(([k, v, id]) => (
        <div key={id} className="flex items-center justify-between px-5 py-3 border-b border-white/[0.04] last:border-0 text-sm">
          <span className="text-gray-300">{k}</span>
          <span className="font-mono text-white" data-testid={id}>{v}</span>
        </div>
      ))}
    </div>
  );
}

function NetworkBadge({ config }) {
  if (!config) return null;
  const live = isMainnet(config);
  const cls = live ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' : 'border-amber-500/30 bg-amber-500/10 text-amber-300';
  return (
    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-[10px] uppercase tracking-widest ${cls}`} data-testid="pricing-network-badge">
      <span className={`w-1.5 h-1.5 rounded-full ${live ? 'bg-emerald-400' : 'bg-amber-400'}`} />
      {live ? 'Live on Base mainnet' : `${config.chain || 'Base'} test mode`}
    </span>
  );
}

function Steps() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="pricing-steps">
      {PAY_STEPS.map((s, i) => (
        <motion.div key={s.n} {...fade(i)} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5" data-testid={`pricing-step-${s.n}`}>
          <div className="text-xs font-mono text-red-300 mb-2">0{s.n}</div>
          <div className="text-sm font-semibold text-white mb-1">{s.title}</div>
          <p className="text-xs text-gray-400 leading-relaxed">{s.body}</p>
        </motion.div>
      ))}
    </div>
  );
}

function Responses() {
  return (
    <div className="space-y-2" data-testid="pricing-responses">
      {PAY_RESPONSES.map((r) => (
        <div key={r.code} className="flex items-start gap-4 rounded-lg border border-white/[0.06] bg-white/[0.02] px-4 py-3" data-testid={`pricing-response-${r.code}`}>
          <code className="text-sm font-mono text-white w-10 shrink-0">{r.code}</code>
          <div><div className="text-sm text-gray-200">{r.label}</div><p className="text-xs text-gray-500">{r.desc}</p></div>
        </div>
      ))}
    </div>
  );
}

function Faq() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="pricing-faq">
      {PAY_FAQ.map((f, i) => (
        <div key={f.q} className="rounded-xl border border-white/[0.06] p-5" data-testid={`pricing-faq-${i}`}>
          <div className="text-sm font-semibold text-white mb-1">{f.q}</div>
          <p className="text-xs text-gray-400 leading-relaxed">{f.a}</p>
        </div>
      ))}
    </div>
  );
}

const H = ({ children }) => <h2 className="text-base md:text-lg font-semibold text-white mt-14 mb-4">{children}</h2>;

export default function DeveloperPricing() {
  const config = useX402Config();
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Helmet><title>Agent Pricing and How to Pay | Kojiru</title></Helmet>
      <Navbar />
      <main className="pt-28 pb-20 px-6">
        <div className="container mx-auto max-w-4xl">
          <Link to="/developers" className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-white transition-colors mb-8" data-testid="pricing-back-link"><ArrowLeft className="w-3.5 h-3.5" /> Developers</Link>
          <motion.div {...fade()}>
            <div className="flex items-center gap-3 mb-5"><Receipt className="w-5 h-5 text-red-300" /><NetworkBadge config={config} /></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-5" data-testid="pricing-heading">Agent pricing</h1>
            <p className="text-sm md:text-base text-gray-400 max-w-2xl leading-relaxed">Agents pay per call in USDC on Base using x402, an HTTP 402 payment standard. No account, no API key, no subscription. Your agent pays in the same request, and refusals are never charged.</p>
          </motion.div>
          <H>Prices</H>
          <PriceTable config={config} />
          {config?.pay_to && <p className="text-[11px] text-gray-500 mt-3 font-mono break-all" data-testid="pricing-treasury">Treasury: {config.pay_to}</p>}
          <div className="mt-8"><GateProposedNotice testid="pricing-gate-proposed-notice" compact /></div>
          <p className="text-xs text-gray-500 mt-3" data-testid="pricing-gate-hidden-note">Payment steps and code for the Binding Action Gate are withdrawn while the gate is a proposed design. Kojiru accepts no gate payments.</p>
          {GATE_OPEN && <>
          <H>How payment works</H>
          <Steps />
          <H>Copy-paste Python example</H>
          <div className="space-y-4">
            <CodeBlock title="Install" code={PAY_INSTALL} lang="bash" testId="pricing-install-code" />
            <CodeBlock title="Python: paid Binding Action Gate" code={PAY_GATE_PY} testId="pricing-python-code" />
          </div>
          <p className="text-xs text-gray-500 mt-3">Need a receipt first? Have the human sign one on <Link to="/binding-gate" className="text-red-300 hover:text-red-200 underline" data-testid="pricing-gate-link">the Binding Action Gate</Link>, or see the receipt format in the <Link to="/developers#binding-gate" className="text-red-300 hover:text-red-200 underline" data-testid="pricing-dev-docs-link">developer docs</Link>.</p>
          <H>Responses</H>
          <Responses />
          <H>Questions</H>
          <Faq />
          </>}
        </div>
      </main>
      <Footer />
    </div>
  );
}
